import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import LegalParagraph from '@/app/components/LegalComponents/LegalParagraph';
import LegalSection, {
  type LegalSectionProps,
} from '@/app/components/LegalComponents/LegalSection';

export interface TermsAndConditionsSectionProps extends LegalSectionProps {
  readonly lastUpdated?: MessageKey;
  readonly localePath: string;
  readonly paragraphs: (
    | MessageKey
    | {
        messageKey: MessageKey;
        className?: string;
        getLink?: (locale: string) => string;
      }
  )[];
  readonly showPreviousTermsLink?: boolean;
  readonly useH1Element?: boolean;
  readonly useH1Variant?: boolean;
}

const TermsAndConditionsSection = ({
  header,
  lastUpdated,
  localePath,
  paragraphs,
  showPreviousTermsLink = false,
  useH1Element = false,
  useH1Variant = false,
}: TermsAndConditionsSectionProps) => {
  const t = useTranslations();

  return (
    <LegalSection
      header={header}
      useH1Element={useH1Element}
      useH1Variant={useH1Variant}
    >
      {showPreviousTermsLink && (
        <div className="mb-8">
          <BodyLink href={`${localePath}previous-terms-and-conditions`}>
            {t('previous_terms_and_conditions')}
          </BodyLink>
        </div>
      )}
      {lastUpdated && (
        <Typography data-cy="terms-and-conditions-last-updated">
          <em>{t(lastUpdated)}</em>
        </Typography>
      )}
      {paragraphs.map((paragraph, index) => {
        return typeof paragraph === 'string' ? (
          <LegalParagraph key={index} tx={paragraph} />
        ) : (
          <LegalParagraph
            className={paragraph.className}
            key={index}
            tx={paragraph.messageKey}
            values={
              paragraph.getLink && {
                link: (chunks) => (
                  <BodyLink
                    external
                    href={paragraph.getLink!(localePath)}
                    target="_blank"
                  >
                    {chunks}
                  </BodyLink>
                ),
              }
            }
          />
        );
      })}
    </LegalSection>
  );
};

export default TermsAndConditionsSection;
